import type { PaymentMethod } from '../utils/paymentMethods';
import type { DarkToneName } from '../theme/refinedDark';
// User roles enum
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  HEAD_OF_ORGANIZATION = 'HEAD_OF_ORGANIZATION',
  CHIEF_OPERATING_OFFICER = 'CHIEF_OPERATING_OFFICER',
  CAMPUS_INCHARGE = 'CAMPUS_INCHARGE',
  ADMINISTRATOR = 'ADMINISTRATOR',
  ACADEMICS_INCHARGE = 'ACADEMICS_INCHARGE',
  EXAMINATION_BOARD_HEAD = 'EXAMINATION_BOARD_HEAD'
}

// Removed old permission enums - now using module-based permissions (read/write)

// Permission interface - Updated to use module-based permissions (read/write)
export interface Permission {
  _id: string;
  name: string;
  module: string;
  action: string; // 'read' or 'write'
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// User Permission interface
export interface UserPermission {
  _id: string;
  userId: string;
  permissionId: Permission;
  centerId: string;
  isGranted: boolean;
  grantedBy: {
    _id: string;
    name: string;
    email: string;
  };
  grantedAt: string;
  createdAt: string;
  updatedAt: string;
}

// Center interface
export interface Center {
  _id: string;
  name: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  logo?: string;
  userCount?: number;
  admissionCount?: number;
  studentCount?: number;
}

// School Settings interface
export interface SchoolSettings {
  timezone: string;
  dateFormat: string;
  currency: string;
  academicYearStart: number;
  /** Day of month (1-28) that fee installments snap to. Default 1. */
  feeBillingDay?: number;
}

// Login type for user authentication
export type LoginType = 'email' | 'userCode';

// User interface
export interface User {
  _id: string;
  schoolId?: {
    _id: string;
    name: string;
    code?: string; // School code for user ID generation
    settings?: SchoolSettings;
    logo?: string;
  };
  roleId?: {
    _id: string;
    name: string;
    permissions?: Permission[] | string[];
    isSystemRole?: boolean;
    // True when this role is the school-owner role. Populated on list
    // endpoints so the Employees UI can lock down School Admin rows
    // (only Name is editable; Deactivate/Reset Password/Delete blocked).
    isSchoolAdmin?: boolean;
  };
  name: string;
  email?: string; // Optional - may not be set for userCode-based users
  userCode?: string; // School-based user ID (format: SCHOOL_CODE-NNNNNN)
  loginType?: LoginType; // 'email' or 'userCode'
  phone?: string;
  isSystemAdmin: boolean; // Super Admin flag (platform owner)
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Admission domain types live in ./admissions.ts (re-exported below)
export * from './admissions';

// Student interface
export interface Student {
  _id: string;
  schoolId: {
    _id: string;
    name: string;
  };
  batchId: {
    _id: string;
    name: string;
    academicYear: string;
  }; // Required - batch is mandatory
  groupId?: {
    _id: string;
    name: string;
  }; // Optional - student group (Pre-Engineering, Pre-Medical, etc.)
  sectionId?: {
    _id: string;
    name: string;
  }; // Optional - section within the active class
  name: string;
  phone: string;
  fatherName: string;
  fatherPhone: string;
  rollNumber: string;
  contact: string;
  address: string;
  // Human-facing student identifier. 6-digit zero-padded, unique per school,
  // immutable once assigned. See backend/src/utils/grNumber.ts.
  grNumber: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Letter and LetterTemplate shapes live in ./letters.ts (LetterV2 /
// LetterTemplateV2). Keep this file lean — no duplicate exports.

// Group interface
export interface Group {
  _id: string;
  centerId: {
    _id: string;
    name: string;
  };
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Grade interface - school-wide grade settings
export interface Grade {
  _id: string;
  schoolId: {
    _id: string;
    name: string;
  };
  name: string; // Grade name (e.g., "A+", "A", "B+", "B", "C", "F")
  minPercentage: number; // Minimum percentage for this grade (inclusive)
  maxPercentage: number; // Maximum percentage for this grade (inclusive)
  color: string; // Hex color code for representing this grade
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// LetterTemplate moved to ./letters.ts as LetterTemplateV2.

// Expense types
export enum ExpenseStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface ExpenseCategory {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
  /** Ledger account chosen for it; null uses `suggestedAccountCode`. */
  glAccountCode?: string | null;
  suggestedAccountCode?: string;
}

export interface ExpenseSubCategory {
  _id: string;
  categoryId: string | { _id: string; name: string };
  name: string;
  description?: string;
  isActive: boolean;
}

export interface Expense {
  _id: string;
  centerId: { _id: string; name: string };
  title: string;
  amount: number;
  date: string;
  categoryId?: { _id: string; name: string };
  subCategoryId?: { _id: string; name: string };
  notes?: string;
  /** How it was paid — decides the cash or bank account the books credit. */
  method?: PaymentMethod;
  referenceNumber?: string;
  /** Disk filename under uploads/expense-documents/. Use toUploadUrl helper to build the URL. */
  documentFilename?: string;
  /** Original name uploaded by the user (for display). */
  documentName?: string;
  documentType?: 'image' | 'pdf';
  status: ExpenseStatus;
  createdBy: { _id: string; name: string };
  approvedBy?: { _id: string; name: string };
  approvedAt?: string;
  rejectedBy?: { _id: string; name: string };
  rejectedAt?: string;
  rejectionReason?: string;
  reversedBy?: { _id: string; name: string };
  reversedAt?: string;
  reversalReason?: string;
  batchId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A policy or SOP: a PDF the school keeps for its staff. Global ones (set by the platform) show in every school. */
export interface LibraryDocument {
  _id: string;
  title: string;
  /** Original name (display). */
  fileName: string;
  /** Disk filename under uploads/policies/ or uploads/sops/. */
  filename: string;
  fileSize: number;
  /** The school it belongs to; absent on a global one. */
  schoolId?: {
    _id: string;
    name: string;
  } | null;
  isGlobal: boolean;
  uploadedBy: {
    _id: string;
    name: string;
    email: string;
  } | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// API Response interface
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Pagination interface
export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Auth context interface
export interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: { name: string; email?: string }) => Promise<void>;
  updateUser: (user: User) => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Theme context interface
export interface ThemeContextType {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
  /** Which refined dark tone dark mode uses. */
  darkTone: DarkToneName;
  setDarkTone: (tone: DarkToneName) => void;
}

// Form interfaces
export interface LoginFormData {
  identifier: string; // Email or User ID (userCode)
  password: string;
}

export interface CenterFormData {
  name: string;
  logo?: File;
}

export interface UserFormData {
  name: string;
  email: string;
  password?: string;
  role: UserRole;
  centerId?: string;
}

export interface StudentFormData {
  name: string;
  fatherName: string;
  class: string;
  rollNumber: string;
  contact: string;
  address: string;
}

export interface FineReceiptFormData {
  amount: number;
  reason: string;
}

// LetterTemplateFormData / GenerateLetterFormData moved to ./letters.ts.

export interface ExpenseFormData {
  title: string;
  amount: number;
  date: string;
  categoryId?: string;
  subCategoryId?: string;
  notes?: string;
  document?: File;
}

export interface ExpenseBulkUploadFormData {
  file: File;
}

// Navigation interface
export interface NavigationItem {
  title: string;
  icon?: React.ReactNode;
  path?: string;
  /** Sub-heading this item sits under inside its parent's submenu. */
  section?: string;
  children?: NavigationItem[];
  requiredRoles?: UserRole[];
}

// Table column interface
export interface TableColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string;
}

// File upload interface
export interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  onUpload: (files: File[]) => void;
  loading?: boolean;
  error?: string;
}

// Notification interface
export interface NotificationProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

// Modal interface
export interface ModalProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  actions?: React.ReactNode;
}

// Dashboard stats interface
export interface DashboardStats {
  totalCenters?: number;
  totalUsers?: number;
  totalAdmissions?: number;
  pendingAdmissions?: number;
  approvedAdmissions?: number;
  rejectedAdmissions?: number;
  totalStudents?: number;
}

// CSV upload result interface
// Notification enums
export enum NotificationLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export enum NotificationModule {
  USER = 'USER',
  CENTER = 'CENTER',
  ADMISSION = 'ADMISSION',
  STUDENT = 'STUDENT',
  LETTER = 'LETTER',
  EXPENSE = 'EXPENSE',
  POLICY = 'POLICY',
  SOP = 'SOP',
  BATCH = 'BATCH',
  PERMISSION = 'PERMISSION'
}

export enum NotificationAction {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
  UPLOADED = 'UPLOADED',
  GENERATED = 'GENERATED'
}

// Notification interfaces
export interface Notification {
  _id: string;
  title: string;
  message: string;
  level: NotificationLevel;
  module: NotificationModule;
  action: NotificationAction;
  centerId?: string;
  centerName?: string;
  actionPerformer: {
    userId: string;
    name: string;
    role: string;
  };
  targetEntity?: {
    id: string;
    name: string;
    type: string;
  };
  metadata?: Record<string, any>;
  isRead: boolean;
  readBy: Array<{
    userId: string;
    readAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

// Class interface
export interface Class {
  _id: string;
  name: string;
  description?: string;
  centerId: {
    _id: string;
    name: string;
  };
  displayOrder?: number;
  isTerminal?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Subject — school-wide entity. Per-class binding lives in CurriculumSubject
// (the Curriculum module owns "what's taught where in which session").
export interface Subject {
  _id: string;
  centerId: {
    _id: string;
    name: string;
  };
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Section interface
export interface Section {
  _id: string;
  classIds: Array<{
    _id: string;
    name: string;
  }>; // Array of classes - empty array means available to all classes
  schoolId: {
    _id: string;
    name: string;
  };
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// BatchClass interface - tracks which class is active for each batch
export interface BatchClass {
  _id: string;
  batchId: {
    _id: string;
    name: string;
    academicYear: string;
  };
  classId: {
    _id: string;
    name: string;
  };
  isActive: boolean;
  startDate: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

// StudentClassHistory interface - tracks student progression through classes
export interface StudentClassHistory {
  _id: string;
  studentId: {
    _id: string;
    name: string;
    rollNumber: string;
  };
  classId: {
    _id: string;
    name: string;
  };
  batchId: {
    _id: string;
    name: string;
    academicYear: string;
  };
  startDate: string;
  endDate?: string;
  isActive: boolean;
  academicYear: string;
  createdAt: string;
  updatedAt: string;
}

// Exam Type interface
export interface ExamType {
  _id: string;
  name: string;
  description?: string;
  /** New exams of this type count in reports unless switched off. */
  countsInReportsByDefault?: boolean;
  schoolId: {
    _id: string;
    name: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Exam Subject configuration
export interface ExamSubject {
  subjectId: string | {
    _id: string;
    name: string;
  };
  maxMarks: number;
}

// Exam Status Type
export type ExamStatus = 'draft' | 'published';

// Exam interface
export interface Exam {
  _id: string;
  name: string;
  examTypeId: string | ExamType;
  schoolId: {
    _id: string;
    name: string;
  };
  // New canonical session pointer. Required after Wave 5.
  sessionId?: string | {
    _id: string;
    name: string;
    status?: string;
  } | null;
  // Legacy — kept for read compat until Wave 5 strips it.
  batchId?: string | {
    _id: string;
    name: string;
    academicYear: string;
  } | null;
  classIds: Array<string | {
    _id: string;
    name: string;
  }>;
  subjects: ExamSubject[];
  startDate: string;
  endDate: string;
  academicYear: string;
  status: ExamStatus;
  publishedAt?: string;
  publishedBy?: string | {
    _id: string;
    name: string;
  };
  isActive: boolean;
  createdBy: string | {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Subject Result Status Enum
//
// NOT_APPEARED means the student wasn't enrolled when this paper was
// administered — distinct from ABSENT (expected, didn't show). Excluded
// from overall % math and rendered as a neutral dash on transcripts.
// Set by admin via Enter Marks for the in-flight exam a mid-session
// enrollment landed inside.
export enum SubjectStatus {
  PENDING      = 'PENDING',
  ABSENT       = 'ABSENT',
  PRESENT      = 'PRESENT',
  NOT_APPEARED = 'NOT_APPEARED',
}

// Result Status Enum
export enum ResultStatus {
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE'
}

// Subject Result within an exam
export interface SubjectResult {
  subjectId: string | {
    _id: string;
    name: string;
  };
  maxMarks: number;
  obtainedMarks: number | null; // null = absent or pending
  percentage: number;
  grade?: string;
  remarks?: string;
  status: SubjectStatus; // PENDING | ABSENT | PRESENT
  isAbsent?: boolean; // @deprecated - use status instead (kept for backward compatibility)
  enteredBy: string | {
    _id: string;
    name: string;
  };
  enteredAt: string;
  isLocked: boolean;
}

// Result interface - stores exam-based results
export interface Result {
  _id: string;
  examId: string | Exam;
  studentId: string | {
    _id: string;
    name: string;
    rollNumber: string;
  };
  classId: string | {
    _id: string;
    name: string;
  };
  // Legacy section/batch (still on schema; new readers use snapshots).
  sectionId?: string | {
    _id: string;
    name: string;
  } | null;
  batchId?: string | {
    _id: string;
    name: string;
    academicYear: string;
  } | null;
  // New canonical fields populated by post-Wave-5 writers.
  enrollmentId?: string | null;
  sessionId?: string | {
    _id: string;
    name: string;
    status?: string;
  } | null;
  sectionInstanceId?: string | {
    _id: string;
    name: string;
  } | null;
  sessionNameSnapshot?: string;
  classNameSnapshot?: string;
  sectionNameSnapshot?: string | null;
  rollNumberSnapshot?: number;
  schoolId: string | {
    _id: string;
    name: string;
  };
  subjectResults: SubjectResult[];
  overallPercentage: number;
  overallGrade?: string;
  resultStatus?: ResultStatus; // COMPLETE | INCOMPLETE
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Form interfaces for new features
export interface ClassFormData {
  name: string;
  description?: string;
  centerId?: string;
}

export interface SubjectFormData {
  centerId?: string;
  name: string;
  description?: string;
}

// Exam Type Form Data
export interface ExamTypeFormData {
  name: string;
  description?: string;
  countsInReportsByDefault?: boolean;
}

// ── Exam Groups ──────────────────────────────────────────────────────────────
// A single logical exam (e.g. "Mid-Term") that spans N classes is stored as N
// rows sharing an examGroupId. The group endpoints operate at the logical
// level so a single user action atomically touches every sibling row.

export interface PerClassSubject {
  subjectId: string;
  maxMarks: number;
}

export interface PerClassEntry {
  classId: string;
  subjects: PerClassSubject[];
}

export interface ExamGroupFormData {
  name: string;
  examTypeId: string;
  sessionId: string;
  startDate: string;
  endDate: string;
  /** Whether the exam appears in transcripts, mark sheets and averages. Defaults from the exam type. */
  countsInReports?: boolean;
  perClass: PerClassEntry[];
}

/** How far an exam's marking has got: papers (one per student per subject), recorded, submitted. */
export interface MarkingProgress {
  papers: number;
  recorded: number;
  locked: number;
}

// One row in the grouped list (returned by GET /exams/groups).
export interface ExamGroupRow {
  examGroupId: string;
  name: string;
  examTypeId: { _id: string; name: string } | null;
  sessionId: { _id: string; name: string; status?: string } | null;
  startDate: string;
  endDate: string;
  status: ExamStatus;
  countsInReports: boolean;
  classes: Array<{ _id: string; name: string }>;
  classCount: number;
  examIds: string[];
  progress: MarkingProgress;
}

export interface ExamGroupSubject {
  subjectId: { _id: string; name: string };
  maxMarks: number;
  papers: number;
  recorded: number;
  locked: number;
  highest: number | null;
}

// Full group view (returned by GET /exams/groups/:groupId).
export interface ExamGroupDetail {
  examGroupId: string;
  name: string;
  examType: { _id: string; name: string; description?: string } | null;
  session: { _id: string; name: string; status?: string } | null;
  startDate: string;
  endDate: string;
  status: ExamStatus;
  countsInReports: boolean;
  publishedAt: string | null;
  progress: MarkingProgress & { students: number };
  perClass: Array<{
    examId: string;
    class: { _id: string; name: string };
    status: ExamStatus;
    progress: MarkingProgress & { students: number };
    subjects: ExamGroupSubject[];
  }>;
}

export interface BatchClassFormData {
  batchId: string;
  classId: string;
}

// Attendance Status Enum
export enum AttendanceStatus {
  PRESENT = 'PRESENT',
  LATE = 'LATE',
  HALF_DAY = 'HALF_DAY',
  ABSENT = 'ABSENT',
  SCHEDULED_OFF = 'SCHEDULED_OFF',
  NOT_SCHEDULED = 'NOT_SCHEDULED',
  LEAVE = 'LEAVE',
  HOLIDAY = 'HOLIDAY'
}

// Information Type Enum (for late, half day, and absent)
export enum InformationType {
  INFORMED = 'INFORMED',
  UNINFORMED = 'UNINFORMED'
}

// Attendance interface
export interface Attendance {
  _id: string;
  centerId: {
    _id: string;
    name: string;
  };
  batchId: {
    _id: string;
    name: string;
    academicYear: string;
  };
  studentId: {
    _id: string;
    name: string;
    rollNumber: string;
    phone: string;
    fatherName: string;
    fatherPhone: string;
  };
  date: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
  markedBy: {
    _id: string;
    name: string;
  };
  markedAt: string;
  updatedBy?: {
    _id: string;
    name: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Attendance Form Data for marking attendance
export interface AttendanceFormData {
  studentId: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

// Bulk attendance form data
export interface BulkAttendanceFormData {
  batchId: string;
  date: string;
  attendanceRecords: AttendanceFormData[];
}

// Attendance statistics interface
export interface AttendanceStatistics {
  batch: {
    _id: string;
    name: string;
    academicYear: string;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
  overallStatistics: {
    present: number;
    late: number;
    halfDay: number;
    absent: number;
  };
  studentStatistics: Array<{
    _id: string;
    student: {
      _id: string;
      name: string;
      rollNumber: string;
    };
    attendanceBreakdown: Array<{
      status: AttendanceStatus;
      count: number;
    }>;
    totalDays: number;
  }>;
}

// Student attendance row for the UI
export interface StudentAttendanceRow {
  student: {
    _id: string;
    name: string;
    rollNumber: string;
    phone: string;
    fatherName: string;
    fatherPhone: string;
  };
  attendance: Attendance | null;
}

// Scheduled Off Types
export enum ScheduledOffType {
  HOLIDAY = 'HOLIDAY',
  EXAM_DAY = 'EXAM_DAY',
  EVENT = 'EVENT',
  MAINTENANCE = 'MAINTENANCE',
  OTHER = 'OTHER'
}

export interface ScheduledOff {
  _id: string;
  centerId: { _id: string; name: string; };
  batchId?: { _id: string; name: string; };
  date: string;
  title: string;
  description?: string;
  type: ScheduledOffType;
  createdBy: { _id: string; name: string; };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Form data interfaces for attendance
export interface AttendanceFormData {
  studentId: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

// Enhanced Attendance interface
export interface Attendance {
  _id: string;
  centerId: { _id: string; name: string; };
  batchId: { _id: string; name: string; academicYear: string; };
  classId: { _id: string; name: string; };
  studentId: { _id: string; name: string; rollNumber: string; phone: string; fatherName: string; fatherPhone: string; };
  date: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
  markedBy: { _id: string; name: string; };
  markedAt: string;
  updatedBy?: { _id: string; name: string; };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
} 